import React, { type MouseEventHandler, useCallback } from 'react';
import {
  AhaButton,
  AhaModal,
  AhaModalBody,
  AhaModalFooter,
  AhaModalHeader,
  AhaSpinner,
} from 'components/common/reactifyWebComponent';
import ReactiveContainer from 'components/common/ReactiveContainer';
import {
  type SharedSnippetMode,
  type SharedSnippetReference,
} from '@aha-app/aha-editor-v2';

interface SharedSnippetsModalProps {
  onClose: () => void;
  onSelectSnippet: (
    sharedSnippet: SharedSnippetReference,
    options: { open: boolean }
  ) => void;
  onCreateSharedSnippet?: (
    mode: SharedSnippetMode
  ) => Promise<SharedSnippetReference | undefined>;
  reactiveUrl: string;
}

const SharedSnippetsModal: React.FC<SharedSnippetsModalProps> = ({
  onClose,
  onSelectSnippet,
  onCreateSharedSnippet,
  reactiveUrl,
}) => {
  const createSnippetHandler = useCallback<(mode: SharedSnippetMode) => void>(
    async mode => {
      if (onCreateSharedSnippet) {
        const sharedSnippetReference = await onCreateSharedSnippet(mode);
        if (sharedSnippetReference) {
          onSelectSnippet(sharedSnippetReference, { open: false });
        }
      }
    },
    [onCreateSharedSnippet, onSelectSnippet]
  );

  const clickHandler = useCallback<MouseEventHandler>(
    event => {
      if (!(event.target instanceof HTMLElement)) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      const createSnippetButton = event.target.closest<HTMLElement>(
        '[data-create-snippet]'
      );

      if (createSnippetButton) {
        createSnippetButton.setAttribute('disabled', 'true');
        createSnippetHandler('block');
        return;
      }

      const sharedSnippetRow = event.target.closest<HTMLElement>(
        '[data-shared-snippet-token]'
      );
      if (sharedSnippetRow?.dataset.sharedSnippetToken) {
        let { sharedSnippetToken, sharedSnippetMode = 'block' } =
          sharedSnippetRow.dataset as {
            sharedSnippetToken: string;
            sharedSnippetMode?: 'block' | 'inline';
          };

        if (!['block', 'inline'].includes(sharedSnippetMode)) {
          sharedSnippetMode = 'block';
        }

        const actionElement =
          event.target.closest<HTMLLinkElement>('a[data-action]');

        if (
          actionElement?.dataset.action &&
          ['edit-snippet', 'view-related'].includes(
            actionElement.dataset.action
          )
        ) {
          window.open(actionElement.href, '_blank');
          return;
        }

        if (event.target.closest('a')) {
          onSelectSnippet(
            {
              token: sharedSnippetToken,
              mode: sharedSnippetMode,
            },
            { open: false }
          );
        }
      }
    },
    [onSelectSnippet, createSnippetHandler]
  );

  const isOwner = window.currentUserRole === 'product_owner';

  return (
    <div onClick={clickHandler}>
      <AhaModal open={open} onClose={onClose}>
        <AhaModalHeader modalTitle='Insert snippet' />
        <AhaModalBody
          style={{
            minWidth: 850,
            height: 700,
            minHeight: 300,
          }}
        >
          <div data-drawer-disabled className='tw-flex tw-flex-col tw-h-full'>
            <ReactiveContainer
              reactivePath='/SharedSnippet-'
              reactiveUrl={reactiveUrl}
              data-reactive-allow-destroy
              className='tw-flex tw-flex-col tw-flex-1'
            >
              <AhaSpinner size='5ex' className='tw-self-center' />
            </ReactiveContainer>
          </div>
        </AhaModalBody>
        <AhaModalFooter>
          <AhaButton slot='secondary_actions' onClick={onClose}>
            Close
          </AhaButton>
          {onCreateSharedSnippet && isOwner && (
            <>
              <AhaButton
                onClick={() => createSnippetHandler('block')}
                kind='secondary'
              >
                Add snippet
              </AhaButton>
            </>
          )}
        </AhaModalFooter>
      </AhaModal>
    </div>
  );
};

export default SharedSnippetsModal;
